import { useAsync, useMountEffect, useSyncedRef } from "@react-hookz/web";
import { useAtomValue } from "jotai";
import first from "lodash/first";
import type { FunctionComponent } from "react";
import { useEffect, useMemo } from "react";
import { fetchQuery, graphql, useRelayEnvironment } from "react-relay";
import type { YouTubePlayer } from "youtube-player/dist/types";

import { data as appData, config } from "shared/data";
import { notEmpty } from "shared/lib/utils";

import { onlyOnePlayingPlayerAtom } from "scmp-app/components/article/article-list/hooks";
import { VideoPlayMode } from "scmp-app/components/scmp-youtube-video/enums";
import type { VideoPlayerYouTubeEventCustomizedParameters } from "scmp-app/components/tracking/google-analytics-4/types";
import type { scmpYoutubeVideoPlayerQuery } from "scmp-app/queries/__generated__/scmpYoutubeVideoPlayerQuery.graphql";

import { getPlayerOptions } from "./helpers";
import {
  useAdIntegration,
  useCheckManualPauseEffect,
  useIntersectionHandler,
  useYouTubeControl,
} from "./hooks";
import {
  AdContainer,
  AdControlButtonContainer,
  Container,
  StyledMuteIcon,
  StyledUnmuteIcon,
  StyledYouTube,
  VideoContainer,
} from "./styles";
import type { VideoOrientation } from "./types";

export type Props = {
  adInfo?: ADInfo;
  allow?: string;
  allowfullscreen?: string;
  className?: string;
  enableAutoPlay: boolean;
  entityId?: string;
  frameborder?: string;
  gaData?: {
    ga4ArticleEntityId?: string;
    ga4Position?: VideoPlayerYouTubeEventCustomizedParameters["position"];
  };
  height?: string;
  index: number;
  isEnabledImaManager?: boolean;
  isMuteVideo?: boolean;
  orientation?: VideoOrientation;
  relatedChannels?: string[];
  title?: string;
  videoId: string;
  videoPlayMode: VideoPlayMode;
  width?: string;
};

export const SCMPYouTubeVideoPlayer: FunctionComponent<Props> = ({
  adInfo,
  className,
  enableAutoPlay,
  entityId,
  gaData,
  height,
  index,
  isEnabledImaManager = true,
  isMuteVideo = true,
  orientation,
  relatedChannels = [appData.youtube.scmpChannelId],
  title,
  videoId,
  videoPlayMode,
  width,
}) => {
  const environment = useRelayEnvironment();
  const [{ result: data }, { execute }] = useAsync(() =>
    fetchQuery<scmpYoutubeVideoPlayerQuery>(
      environment,
      graphql`
        query scmpYoutubeVideoPlayerQuery($applicationId: String!, $entityId: String!) {
          content(
            contentType: VIDEO
            filter: { entityId: $entityId, applicationId: $applicationId }
          ) {
            headline
            sectionsV2 {
              value {
                ... on VideoSection {
                  name
                }
              }
            }
          }
        }
      `,
      {
        applicationId: appData.application.scmp.entityUuid,
        entityId: entityId ?? "",
      },
      {
        fetchPolicy: "store-or-network",
      },
    ).toPromise(),
  );

  useMountEffect(() => {
    if (!notEmpty(entityId)) return;
    void execute();
  });

  // Can play same video in different places
  const uniqueVideoId = ["yt", videoId, entityId, index].filter(notEmpty).join("-");
  const adElementId = `ad-${uniqueVideoId}`;

  const trackingData = useMemo(
    () => ({
      dimension: height && width ? `${width} x ${height}` : undefined,
      ga4ArticleEntityId: gaData?.ga4ArticleEntityId,
      ga4Position: gaData?.ga4Position ?? "article_body",
      sectionString:
        first(data?.content?.sectionsV2)
          ?.value?.map(section => section?.name)
          .join("|") ?? "",
      videoEntityId: entityId ?? "",
      youTubeVideoId: videoId,
      youTubeVideoTitle: title ?? "",
    }),
    [
      height,
      width,
      data?.content?.sectionsV2,
      entityId,
      gaData?.ga4ArticleEntityId,
      gaData?.ga4Position,
      title,
      videoId,
    ],
  );

  const { handleOnEnd, handleOnReady, handleOnStateChange, player } = useYouTubeControl(
    uniqueVideoId,
    trackingData,
  );

  const zone = adInfo?.zone ?? `video/article/instream1`;
  const adIu = `${config.advertisement.googlePublisherTag.networkId}/${config.advertisement.googlePublisherTag.adUnitPrefix}/${zone}`;

  const { stopYoutubeAutoplay } = useAtomValue(onlyOnePlayingPlayerAtom);
  const isManualPlay = videoPlayMode === VideoPlayMode.Manual;
  const wrappedEnableAutoplay = useMemo(
    () => (enableAutoPlay || isManualPlay) && !stopYoutubeAutoplay,
    [enableAutoPlay, isManualPlay, stopYoutubeAutoplay],
  );

  const {
    adsManager,
    handleChangeVolume,
    initImaManager,
    isAdPlaying,
    isAdStarted,
    isImaInitialized,
    isMute,
  } = useAdIntegration({
    adData: {
      adInfo,
      iu: adIu,
    },
    orientation,
    player,
    trackingData,
  });

  const {
    captureIntersectionElement,
    handleIsIntersecting,
    handleIsNotIntersecting,
    intersection,
  } = useIntersectionHandler({
    adsManager,
    isAdPlaying,
    isAdStarted,
    isManualPlay,
    player,
  });

  const {
    handleOnYoutubeInteractEnd,
    handleOnYoutubeInteractStart,
    handleOnYoutubePlayerStateChange: handleManualPauseEffectOnStateChange,
    isManualPaused,
  } = useCheckManualPauseEffect({
    adsManager,
    isAdStarted,
  });

  const latestIsManualPausedReference = useSyncedRef(isManualPaused);
  useEffect(() => {
    const isIntersectionAndAdAndPlayerReady = intersection && isImaInitialized && player;
    if (!isIntersectionAndAdAndPlayerReady) {
      return;
    }

    if (!intersection.isIntersecting) {
      handleIsNotIntersecting();
      return;
    }

    if (intersection.isIntersecting) {
      if (isManualPlay) {
        handleIsIntersecting();
      } else {
        const shouldTriggerIntersect =
          wrappedEnableAutoplay && !latestIsManualPausedReference.current;
        if (shouldTriggerIntersect) handleIsIntersecting();
      }
    }
  }, [
    handleIsIntersecting,
    handleIsNotIntersecting,
    intersection,
    isImaInitialized,
    isManualPlay,
    latestIsManualPausedReference,
    player,
    wrappedEnableAutoplay,
  ]);

  return (
    <Container $orientation={orientation} className={className}>
      <VideoContainer
        onMouseLeave={handleOnYoutubeInteractEnd}
        onMouseOver={handleOnYoutubeInteractStart}
        onTouchEnd={handleOnYoutubeInteractEnd}
        onTouchStart={handleOnYoutubeInteractStart}
        ref={captureIntersectionElement}
      >
        <StyledYouTube
          id={uniqueVideoId}
          onEnd={handleOnEnd}
          onReady={async (event: { target: YouTubePlayer }) => {
            if (isMuteVideo) await event.target.mute();
            if (isEnabledImaManager) await initImaManager(event.target, uniqueVideoId, adElementId);
            handleOnReady(event);
          }}
          onStateChange={(event: { data: number }) => {
            void handleOnStateChange(event);
            handleManualPauseEffectOnStateChange(event);
          }}
          opts={getPlayerOptions({
            adIu,
            enableAutoPlay: wrappedEnableAutoplay,
            height,
            isEnabledImaManager,
            relatedChannels,
            width,
          })}
          title={data?.content?.headline ?? ""}
          videoId={videoId}
        />
        <AdContainer id={adElementId} />
        {isAdStarted && (
          <AdControlButtonContainer>
            {isMute ? (
              <StyledUnmuteIcon onClick={() => handleChangeVolume(false)} />
            ) : (
              <StyledMuteIcon onClick={() => handleChangeVolume(true)} />
            )}
          </AdControlButtonContainer>
        )}
      </VideoContainer>
    </Container>
  );
};

SCMPYouTubeVideoPlayer.displayName = "SCMPYouTubeVideoPlayer";
